import { useEffect, useState, useRef } from 'react';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

const POLLING_INTERVAL = 2000; // 2 seconds
const MAX_POLLING_ATTEMPTS = 300; // 10 minutes total (300 * 2 seconds)

export const useReportXml = (
  reportId: number | null,
  publicToken?: string | null,
) => {
  const [xml, setXml] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingAttemptsRef = useRef(0);
  const timeoutIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (!reportId && !publicToken) {
      setXml(null);
      return;
    }

    let isCancelled = false;
    pollingAttemptsRef.current = 0;

    const fetchXml = async () => {
      if (isCancelled) return;

      setIsLoading(true);
      setError(null);

      try {
        const pathParams = publicToken
          ? { public_token: publicToken }
          : { report_id: reportId };
        const fetcher = publicToken ? fetch : fetchWithCsrf;
        const response = await fetcher(
          routes.api_v0_citeright_get_report_xml_path(pathParams),
          { method: 'GET' },
        );

        if (isCancelled) return;

        if (response.status === 202) {
          // Document is still being processed, retry
          pollingAttemptsRef.current += 1;

          if (pollingAttemptsRef.current < MAX_POLLING_ATTEMPTS) {
            timeoutIdRef.current = window.setTimeout(
              fetchXml,
              POLLING_INTERVAL,
            );
          } else {
            setError('Document processing timed out');
            setIsLoading(false);
          }
          return;
        }

        if (response.status === 404) {
          setError('Document not found');
          setIsLoading(false);
          return;
        }

        if (!response.ok) {
          setError('Failed to fetch document');
          setIsLoading(false);
          return;
        }

        const data = await response.json();

        if (!isCancelled) {
          setXml(data.xml);
          setIsLoading(false);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setIsLoading(false);
        }
      }
    };

    fetchXml();

    return () => {
      isCancelled = true;
      if (timeoutIdRef.current !== null) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [reportId, publicToken]);

  return { xml, isLoading, error };
};
